import { useStore } from '@nanostores/react';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { atom } from 'nanostores';
import { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { PlainButton } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { getStorageValue } from '../../hooks/browser/use-local-storage';
import { SIDEBAR_PORTAL_FADE_ANIMATE, sidebarPortalFadeInitial, sidebarPortalFadeTransition } from '@/utils/motion';
import clsx from 'clsx';
import * as styles from './index.css';
import { SECTION_EXIT_DURATION_MS, SIDEBAR_ANIMATION_DURATION } from './sidebar-timing';

const DEFAULT_SIDEBAR_WIDTH = 370;
const MIN_SIDEBAR_WIDTH = 370;
const MAX_SIDEBAR_WIDTH = 740;
const SIDEBAR_WIDTH_STORAGE_KEY = 'sidebar-width';

function clampSidebarWidth(width: number): number {
  return Math.min(Math.max(width, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH);
}

function readStoredSidebarWidth(): number {
  const stored: unknown = getStorageValue(SIDEBAR_WIDTH_STORAGE_KEY, DEFAULT_SIDEBAR_WIDTH);
  return typeof stored === 'number' && Number.isFinite(stored) ? clampSidebarWidth(stored) : DEFAULT_SIDEBAR_WIDTH;
}

const targetId = 'sidebar-portal';
const activeId = atom<string | null>(null);
const closingId = atom<string | null>(null);
const $sidebarWidth = atom<number>(readStoredSidebarWidth());
const $companionIds = atom(new Map<string, number>());
const $suppressedIds = atom(new Map<string, number>());
/** The one sidebar, if any, whose open panel should draw the eye. */
const $attentionId = atom<string | null>(null);

export function open(id: string) {
  activeId.set(id);
  closingId.set(null);
  if ($attentionId.get() !== id) {
    $attentionId.set(null);
  }
}

export async function close() {
  $attentionId.set(null);
  closingId.set(activeId.get());
  // Wait for section exit animations (e.g. help sidebar stagger) so they are visible before the panel closes.
  await new Promise((resolve) => setTimeout(resolve, SECTION_EXIT_DURATION_MS));
  activeId.set(null);
  await new Promise((resolve) => setTimeout(resolve, SIDEBAR_ANIMATION_DURATION));
  closingId.set(null);
}

export function getActiveSidebarId(): string | null {
  return activeId.get();
}

function setSidebarWidth(width: number): void {
  $sidebarWidth.set(clampSidebarWidth(width));
}

function persistSidebarWidth(): void {
  try {
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, JSON.stringify($sidebarWidth.get()));
  } catch {}
}

export function expandSidebarToMax() {
  setSidebarWidth(MAX_SIDEBAR_WIDTH);
}

/**
 * Asks for the person's attention on a sidebar: the copilot waiting on an
 * answer, say. Only that sidebar's open panel shows it, and it clears on any
 * pointer down inside the panel, when the panel closes, or when another
 * sidebar opens in its place.
 */
export function setSidebarAttention(id: string | null) {
  $attentionId.set(id);
}

export function toggle(id: string) {
  if (activeId.get() === id) {
    close();
  } else {
    open(id);
  }
}

// expose the sidebar functions to the window object
// so that the ember ui can hook into it and open/close the sidebar
// to prevent having two sidebars open at the same time
Object.assign(window, {
  __hydra_sidebar__: {
    open,
    close,
    toggle,
    subscribe: activeId.subscribe,
    openSupportForm: () => {
      // No-op placeholder. Replaced at runtime by HelpSidebar once it mounts.
    },
  } satisfies Window['__hydra_sidebar__'],
});

/**
 * Renders the sidebar root element.
 * This is the element that will be used as the target for the sidebar portal.
 */
const COMPANION_BOX_SHADOW = '-4px 2px 2px 0px #34344603, -12px 4px 24px 0px #34344629';

/**
 * Hides a sidebar's panel while keeping it the active sidebar, so it reappears
 * once suppression lifts. Closing instead would discard `activeId`, leaving
 * nothing to restore on back-navigation.
 */
export function useSuppressSidebar(sidebarId: string, isSuppressed: boolean) {
  // Layout effect so the panel is hidden/restored before the browser paints —
  // a plain effect leaves one painted frame of stale width.
  useLayoutEffect(() => {
    if (!isSuppressed) {
      return;
    }

    // Counted, like $companionIds: two suppressors of one id must not un-hide
    // the panel when the first of them unmounts.
    const next = new Map($suppressedIds.get());
    next.set(sidebarId, (next.get(sidebarId) ?? 0) + 1);
    $suppressedIds.set(next);

    return () => {
      const remaining = new Map($suppressedIds.get());
      const count = (remaining.get(sidebarId) ?? 1) - 1;
      if (count <= 0) {
        remaining.delete(sidebarId);
      } else {
        remaining.set(sidebarId, count);
      }
      $suppressedIds.set(remaining);
    };
  }, [sidebarId, isSuppressed]);
}

/** Non-reactive read, for callers outside React (analytics, event handlers). */
export function isSidebarSuppressed(sidebarId: string) {
  return $suppressedIds.get().has(sidebarId);
}

function useIsSuppressed(sidebarId: string | null) {
  const suppressedIds = useStore($suppressedIds);
  return sidebarId != null && suppressedIds.has(sidebarId);
}

/**
 * A surface that sits beside a sidebar rather than over it.
 *
 * `isActive` is for a companion that comes and goes while its page stays —
 * a drawer that stays mounted to keep its slide-out, say. Claiming
 * companionship restyles the sidebar (square corners, up over the top nav,
 * a left border), so a surface that is not on screen must not claim it.
 */
export function useSidebarCompanion(sidebarId: string, isActive = true) {
  const offset = useSidebarOffset(sidebarId);
  const isOpen = isActive && offset > 0;

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const map = new Map($companionIds.get());
    map.set(sidebarId, (map.get(sidebarId) ?? 0) + 1);
    $companionIds.set(map);
    return () => {
      const map = new Map($companionIds.get());
      const count = (map.get(sidebarId) ?? 1) - 1;
      if (count <= 0) {
        map.delete(sidebarId);
      } else {
        map.set(sidebarId, count);
      }
      $companionIds.set(map);
    };
  }, [sidebarId, isActive]);

  return {
    style: {
      maxWidth: isOpen ? `calc(90vw - ${offset}px)` : undefined,
      right: isOpen ? offset : undefined,
      transition: `right ${SIDEBAR_ANIMATION_DURATION}ms ease, max-width ${SIDEBAR_ANIMATION_DURATION}ms ease`,
      boxShadow: isOpen ? COMPANION_BOX_SHADOW : undefined,
      height: isOpen ? '100vh' : undefined,
      top: isOpen ? 0 : undefined,
    },
    shouldCloseOnOverlayClick: !isOpen,
    withOverlay: !isOpen,
  };
}

/** Drag-to-resize: owns the persisted width and the document-level drag listeners. */
function useSidebarResize() {
  const sidebarWidth = useStore($sidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (sidebarRef.current == null) return;

      const rect = sidebarRef.current.getBoundingClientRect();
      setSidebarWidth(rect.right - event.clientX);
    };

    const handleMouseUp = () => {
      persistSidebarWidth();
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsResizing(true);
  };

  return { sidebarRef, sidebarWidth, isResizing, handleMouseDown };
}

type PublishSidebarOffsetArgs = {
  companion: boolean;
  isOpen: boolean;
  sidebarWidth: number;
};

/**
 * Publishes the space the open panel actually occupies as a global CSS variable so
 * fixed, right-anchored chrome (e.g. the CDP save bar / `Affix`) can offset its right
 * edge and keep its actions clear of the panel -- mirrors `--hydra-main-nav-width` on
 * the left.
 *
 * The panel's own width is `sidebarWidth`, but `sidebarRootLayoutRefresh` also pushes
 * it in by a `space['150']` right margin (reset to 0 in companion mode); without that
 * term the offset lands inside the panel and the strip stays covered, since the panel
 * (z-index 40) and its resize handle (41) both paint over the affix (39). Zero when
 * nothing is open or the panel is suppressed.
 */
function usePublishSidebarOffset({ companion, isOpen, sidebarWidth }: PublishSidebarOffsetArgs) {
  const { setVariable, removeVariable } = useThemeContext();

  // Layout effect so the variable exists before first paint: consumers have a 0px
  // CSS fallback, which would otherwise flash the bar under the panel for a frame.
  useLayoutEffect(() => {
    const occupiedWidth = companion ? `${sidebarWidth}px` : `calc(${sidebarWidth}px + ${themeVars.space['150']})`;
    setVariable(GlobalCSSVariable.SidebarOffsetWidth, isOpen ? occupiedWidth : '0px');
  }, [companion, isOpen, setVariable, sidebarWidth]);

  useEffect(
    () => () => {
      removeVariable(GlobalCSSVariable.SidebarOffsetWidth);
    },
    [removeVariable],
  );
}

/** The root props that draw the attention ring; nothing for a panel that wasn't asked for it. */
function useAttentionRootProps(activeSidebarId: string | null, isOpen: boolean) {
  const attentionId = useStore($attentionId);
  if (!isOpen || activeSidebarId !== attentionId) {
    return { className: undefined };
  }
  return {
    className: styles.sidebarRootAttention,
    'data-attention': true,
    // Any interaction with the panel means it has been noticed.
    onPointerDownCapture: () => $attentionId.set(null),
  };
}

export function SidebarRoot() {
  const _activeSidebarId = useStore(activeId);
  const isSuppressed = useIsSuppressed(_activeSidebarId);
  const isOpen = _activeSidebarId !== null && !isSuppressed;
  const companionIds = useStore($companionIds);
  const companion = _activeSidebarId !== null && companionIds.has(_activeSidebarId);
  const { className: attentionClassName, ...attentionProps } = useAttentionRootProps(_activeSidebarId, isOpen);
  const shouldReduceMotion = useReducedMotion();
  const [display, setDisplay] = useState<'block' | 'none'>('none');
  const { sidebarRef, sidebarWidth, isResizing, handleMouseDown } = useSidebarResize();
  usePublishSidebarOffset({ companion, isOpen, sidebarWidth });

  return (
    <>
      {isOpen ? (
        <PlainButton
          className={styles.resizeHandleLayoutRefresh}
          onMouseDown={handleMouseDown}
          aria-label="Resize sidebar"
        />
      ) : null}
      <AnimatePresence>
        <m.div
          ref={sidebarRef}
          animate={{
            width: isOpen ? sidebarWidth : 0,
            display,
          }}
          role="complementary"
          initial={{ width: 0 }}
          // putting 0 causes a slight scroll on the html element
          // when the sidebar is closed, so we use display: none instead
          // and set the width to 0
          onAnimationStart={() => {
            if (isOpen) {
              setDisplay('block');
            }
          }}
          onAnimationComplete={() => {
            if (!isOpen) {
              setDisplay('none');
            }
          }}
          exit={{
            display: 'none',
          }}
          transition={{
            duration: isResizing || shouldReduceMotion === true ? 0 : SIDEBAR_ANIMATION_DURATION / 1000,
            ease: 'easeInOut',
          }}
          style={{
            zIndex: 40, // pluma navbar z-index
            position: 'relative',
          }}
          {...attentionProps}
          className={clsx(
            styles.sidebarRoot,
            styles.sidebarRootLayoutRefresh,
            companion && styles.sidebarRootCompanion,
            attentionClassName,
          )}
          id={targetId}
        />
      </AnimatePresence>
    </>
  );
}

type SidebarProps = {
  id: string;
  children: React.ReactNode;
  onToggle?: (isOpen: boolean) => void;
  onToggleComplete?: (isOpen: boolean) => void;
  ref?: Ref<HTMLDivElement>;
};

export function Sidebar({ id, children, onToggle, onToggleComplete, ref }: SidebarProps) {
  const _activeSidebarId = useStore(activeId);
  const _closingSidebarId = useStore(closingId);
  const isSuppressed = useIsSuppressed(id);
  const isClosing = _closingSidebarId === id;
  const shouldReduceMotion = useReducedMotion();
  const sidebarTarget = document.getElementById(targetId);
  const lastActiveId = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

  useEffect(() => {
    const wasActive = lastActiveId.current === id;
    const isActive = _activeSidebarId === id;

    if (lastActiveId.current !== _activeSidebarId) {
      lastActiveId.current = _activeSidebarId;

      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      if (wasActive && !isActive) {
        onToggle?.(false);
      }

      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      if (!wasActive && isActive) {
        onToggle?.(true);
      }
    }
  }, [_activeSidebarId, id, onToggle]);

  if (_activeSidebarId !== id && _closingSidebarId !== id) {
    return null;
  }

  if (isSuppressed) {
    return null;
  }

  if (sidebarTarget == null) {
    return null;
  }

  // Fade only on the portal root so nested sidebars can own vertical stagger without compounding motion.
  const transition = sidebarPortalFadeTransition(isClosing, shouldReduceMotion);
  const sidebarContentInitial = sidebarPortalFadeInitial(shouldReduceMotion);

  return createPortal(
    <m.div
      ref={containerRef}
      className={styles.sidebar}
      initial={sidebarContentInitial}
      animate={SIDEBAR_PORTAL_FADE_ANIMATE}
      transition={transition}
      onAnimationComplete={() => {
        onToggleComplete?.(!isClosing);
      }}
    >
      {children}
    </m.div>,
    sidebarTarget,
  );
}

/**
 * Whether this sidebar is the open one, even while its panel is hidden by
 * suppression. For state that must survive the panel being masked — the agent
 * session in the URL would otherwise be cleared on the way out of /agent/*.
 */
export function useSidebarOpen(id: string) {
  const active = useStore(activeId) === id;
  const closing = useStore(closingId) === id;

  return active || closing;
}

/**
 * Whether the sidebar is actually on screen. A suppressed sidebar reads as
 * inactive so "close the panel" affordances stay hidden — acting on one would
 * discard the open state that suppression exists to preserve.
 */
export function useSidebarActive(id: string) {
  const active = useStore(activeId) === id;
  const closing = useStore(closingId) === id;
  const isSuppressed = useIsSuppressed(id);

  return (active || closing) && !isSuppressed;
}

/**
 * Returns whether the sidebar with the given id is currently in its closing state
 * (after close() was called but before the close animation completes).
 */
export function useSidebarClosing(id: string) {
  return useStore(closingId) === id;
}

export function useSidebarOffset(id: string) {
  const isActive = useStore(activeId) === id;
  const isSuppressed = useIsSuppressed(id);
  const width = useStore($sidebarWidth);
  return isActive && !isSuppressed ? width : 0;
}

export { SidebarHeader, type SidebarCloseControl } from './sidebar-header';
